import { resolvePostLoginDestination } from "../../utils/navigation";
import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate, useLocation, Link } from "react-router-dom";
import LdapStatusComponent from "./LdapStatusComponent";
import { sanitizeInput, validateInput } from "../../utils/inputValidation";

// Icon components
const EyeIcon = ({ className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    fill="none"
    viewBox="0 0 24 24"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M1.5 12s3.5-7 10.5-7 10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EyeOffIcon = ({ className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    fill="none"
    viewBox="0 0 24 24"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M3 3l18 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9.88 9.88A3.5 3.5 0 0 0 12 8.5c1.93 0 3.5 1.57 3.5 3.5 0 .75-.24 1.45-.65 2.02"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.11 6.11C3.86 7.68 2.4 9.7 1.5 12c0 0 3.5 7 10.5 7 2.15 0 4-.47 5.61-1.26M14.12 14.12A3.5 3.5 0 0 1 12 15.5c-1.93 0-3.5-1.57-3.5-3.5 0-.46.09-.9.26-1.3"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M22.5 12s-1.05-2.13-3.21-3.98"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Login = () => {
  const [formData, setFormData] = useState({
    usernameOrEmail: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const location = useLocation();

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Sanitize input as user types
    const sanitizedValue = sanitizeInput(value);

    setFormData((prev) => ({
      ...prev,
      [name]: sanitizedValue,
    }));
    // Clear errors when user types
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.usernameOrEmail.trim()) {
      newErrors.usernameOrEmail = "Username or email is required";
    } else {
      // Check for malicious input
      const validation = validateInput(formData.usernameOrEmail);
      if (!validation.isValid) {
        newErrors.usernameOrEmail = "Invalid input detected";
      }
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length > 100) {
      newErrors.password = "Password is too long";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    const result = await login(formData);

    if (result.success) {
      navigate(resolvePostLoginDestination(result.user, location.state?.from), { replace: true });
    } else {
      setErrors({ submit: result.message });
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-green-cloud flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="card-brand p-8 animate-fade-in">
          {/* Header */}
          <div className="text-center mb-8">
            {/* Logo */}
            <img
              src="/Journey_Logo.png"
              alt="Journey Logo"
              className="mx-auto mb-4 h-36 object-contain"
            />

            <h2 className="text-xl font-semibold text-grey-dusk mb-2">
              Sign In
            </h2>
            <div className="space-y-2">
              <p className="text-gray-600">Sign in with your credentials</p>
              <div className="flex items-center justify-center space-x-2">
                <LdapStatusComponent compact={true} />
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="usernameOrEmail" className="label-brand">
                Username
              </label>
              <input
                type="text"
                id="usernameOrEmail"
                name="usernameOrEmail"
                value={formData.usernameOrEmail}
                onChange={handleChange}
                className={`input-brand ${
                  errors.usernameOrEmail ? "input-brand-error" : ""
                }`}
                disabled={loading}
                placeholder="Enter your username or email"
              />
              {errors.usernameOrEmail && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.usernameOrEmail}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="label-brand">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`input-brand pr-12 ${
                    errors.password ? "input-brand-error" : ""
                  }`}
                  disabled={loading}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-grey-dusk transition-colors duration-200"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-red-600 mt-1">{errors.password}</p>
              )}
              {/* Reset options removed: password recovery is now staff-only from the student directory. */}
            </div>

            {errors.submit && (
              <div className="alert-brand-error">{errors.submit}</div>
            )}

            <button
              type="submit"
              className={`btn-brand-primary w-full ${
                loading ? "opacity-75 cursor-not-allowed" : ""
              }`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Signing in...
                </div>
              ) : (
                "Sign In"
              )}
            </button>

            <div className="mt-6 pt-6 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-600">
                Don't have an account?{" "}
                <Link
                  to="/register"
                  className="font-semibold text-sunrise-red hover:text-sunrise-600 transition-colors duration-200"
                >
                  Register here
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
